import { Box, Button, Group, Pagination, Select, Table, Title } from "@mantine/core";
import type { JSX } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "urql";

import Loading from "../Loading";
import DeleteButton from "./DeleteButton";
import classes from "./MissingInformation.module.css";
import {
    type MissingInformationPageQuery,
    type MissingInformationPageQueryVariables,
    type MissingInformationPersonalityNamesQuery,
    missingInformationPageQuery,
    personalityNamesQuery,
} from "./queries";

function MissingInformationRecords(): JSX.Element {
    const [page, setPage] = useState(1);
    const [selectedPersonality, setSelectedPersonality] = useState<string | null>(null);

    const [personalityNamesResult] = useQuery<MissingInformationPersonalityNamesQuery>({
        query: personalityNamesQuery,
    });

    const handlePersonalityChange = (value: string | null) => {
        setSelectedPersonality(value);
        setPage(1);
    };

    const personalityName = selectedPersonality || null;

    const [result] = useQuery<MissingInformationPageQuery, MissingInformationPageQueryVariables>({
        query: missingInformationPageQuery,
        variables: {
            page,
            personalityName,
        },
    });

    const personalityOptions = [
        { value: "", label: "All personalities" },
        ...(personalityNamesResult.data?.missingInformationPersonalityNames || []).map((name) => ({
            value: name,
            label: name,
        })),
    ];

    let main = null;
    if (result.fetching || personalityNamesResult.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else if (!result.data || result.data.missingInformationPage.maxPage === 0) {
        main = <Box>No missing information records found</Box>;
    } else {
        main = (
            <>
                <Pagination total={result.data.missingInformationPage.maxPage} value={page} onChange={setPage} />
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Created</Table.Th>
                            <Table.Th>Topic</Table.Th>
                            <Table.Th>AI Personality</Table.Th>
                            <Table.Th colSpan={2} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {result.data.missingInformationPage.items.map((record) => (
                            <Table.Tr key={record.id}>
                                <Table.Td>{new Date(record.created).toLocaleString("en-GB")}</Table.Td>
                                <Table.Td>{record.topic}</Table.Td>
                                <Table.Td>{record.personalityName}</Table.Td>
                                <Table.Td>
                                    <Button component={Link} to={`${record.id}/`}>
                                        Details
                                    </Button>
                                </Table.Td>
                                <Table.Td>
                                    <DeleteButton record={record} />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
                <Pagination total={result.data.missingInformationPage.maxPage} value={page} onChange={setPage} />
            </>
        );
    }

    return (
        <Box className={classes.grid}>
            <Group justify="space-between">
                <Title order={2}>Missing Information Records</Title>
                <Select
                    label="Filter by AI Personality"
                    placeholder="All personalities"
                    data={personalityOptions}
                    value={selectedPersonality || ""}
                    onChange={handlePersonalityChange}
                    clearable
                    w={250}
                />
            </Group>
            {main}
        </Box>
    );
}

export default MissingInformationRecords;
